import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BenefitDTOBenefitEnum, BulkActivationUpdateResponseDTO, UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { extractBulkErrorResults } from '../bulkActivation/extractBulkErrorResults';
import { useRefetchActivations } from '../query';
import { PauseMutationDTO } from './useBulkPauseActivationsMutation';

function extractDeactivationBlock(err: unknown): BulkActivationUpdateResponseDTO | undefined {
  if (!(err instanceof AxiosError) || err.response?.status !== 422) {
    return undefined;
  }
  const data = err.response.data?.data as { deactivationBlocked?: boolean; endDate?: string } | undefined;
  if (!data?.deactivationBlocked) {
    return undefined;
  }
  return { results: [{ status: 'BLOCKED', endDate: data.endDate }] };
}

export function useSingleUpdateActivationMutation(employeeId: string, activationId: string) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchActivations = useRefetchActivations(employeeId);

  return useMutation({
    mutationFn: async (update: UpdateBenefitActivationDTO): Promise<BulkActivationUpdateResponseDTO> => {
      try {
        const result = await apis.benefits
          .updateBenefitActivation({
            tenantId: getTenantId(),
            employeeId,
            benefitActivationId: activationId,
            updateBenefitActivationDTO: update,
          })
          .then(res => res.data);
        return {
          results: [{ status: result.adjustedEndDate ? 'ADJUSTMENT' : 'SUCCESS', endDate: result.adjustedEndDate }],
        };
      } catch (err) {
        const blocked = extractDeactivationBlock(err);
        if (blocked) {
          return blocked;
        }
        throw err;
      }
    },
    onError: (err: Error) => {
      withMessage(err);
    },
    onSuccess: async () => {
      await refetchActivations();
    },
  });
}

export function useSinglePauseActivationMutation(benefit: BenefitDTOBenefitEnum, employeeId: string, customBenefitId?: string) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchActivations = useRefetchActivations(employeeId);

  return useMutation({
    mutationFn: async (pauseDTO: PauseMutationDTO): Promise<BulkActivationUpdateResponseDTO> => {
      try {
        return await apis.benefits
          .pauseBenefits({
            tenantId: getTenantId(),
            pauseBenefitActivationsDTO: {
              benefit,
              customBenefitId,
              employeeIds: [employeeId],
              endCurrentActivation: pauseDTO.endCurrentActivation,
              startNewActivation: pauseDTO.startNewActivation,
            },
          })
          .then(res => res.data);
      } catch (err) {
        const errorResults = extractBulkErrorResults(err);
        if (errorResults) {
          return { results: errorResults };
        }
        throw err;
      }
    },
    onError: (err: Error) => {
      withMessage(err);
    },
    onSuccess: async () => {
      await refetchActivations();
    },
  });
}
